import { useEffect, useState } from "react";
import { FileDown, FileText, Upload, Plus, Trash2, Landmark } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { deltaVs, pctFmt } from "./categories";
import ImportModal from "./ImportModal";
import CaYearModal from "./CaYearModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const COLS = [
  ["encaisse", "CA encaissé"], ["dep_pro", "Dép. pro"], ["urssaf", "URSSAF"],
  ["impots_provisions", "Impôts prov."], ["resultat_pro", "Résultat pro"],
  ["dep_perso", "Dép. perso"], ["reste_a_vivre", "Reste à vivre"],
];
const frMonth = (key) => new Date(`${key}-15`).toLocaleDateString("fr-FR", { month: "long" });

function DeltaChip({ cur, prev }) {
  const d = deltaVs(cur, prev);
  if (d.pct === null) return <span className="text-[10px] text-neutral-300">—</span>;
  const up = d.pct >= 0;
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"}`}>{pctFmt(d.pct)}</span>;
}

export default function BilanTab({ year }) {
  const [data, setData] = useState(null);
  const [importKind, setImportKind] = useState(null); // "expenses" | "income"
  const [caModal, setCaModal] = useState(false);
  const [income, setIncome] = useState({ month: "", amount: "" });
  const load = () => api.get("/pilotage/bilan", { params: { year } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setData(null); load(); }, [year]); // eslint-disable-line

  const download = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e)); }
  };
  const addIncome = async () => {
    if (!income.month || !(+income.amount > 0)) { toast.error("Mois et montant requis"); return; }
    if (!income.month.startsWith(`${year}-`)) { toast.error(`Le mois doit appartenir à l'année ${year} affichée`); return; }
    try {
      await api.post("/pilotage/income-manual", { month: income.month, amount: +income.amount });
      toast.success("CA historique enregistré"); setIncome({ month: "", amount: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delIncome = async (id) => {
    try { await api.delete(`/pilotage/income-manual/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <p className="py-10 text-center text-xs text-neutral-400">Chargement…</p>;
  const { totals, prev_year_totals: prev } = data;
  return (
    <div className="space-y-4" data-testid="bilan-tab">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-neutral-500 flex-1">Bilan {year} — encaissements et dépenses payées, comparaison avec {year - 1}.</p>
        <button onClick={() => download(`/pilotage/export/expenses.csv?frm=${year}-01-01&to=${year}-12-31`, `depenses_${year}.csv`)}
          className="flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bilan-export-expenses">
          <FileDown className="w-3.5 h-3.5" style={{ color: VIOLET }} /> Export CSV dépenses
        </button>
        <button onClick={() => download(`/pilotage/export/bilan.csv?year=${year}`, `bilan_${year}.csv`)}
          className="flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bilan-export-csv">
          <FileDown className="w-3.5 h-3.5" style={{ color: VIOLET }} /> Export CSV bilan
        </button>
        <button onClick={() => download(`/pilotage/export/bilan.pdf?year=${year}`, `bilan_${year}.pdf`)}
          className="flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bilan-export-pdf">
          <FileText className="w-3.5 h-3.5" style={{ color: VIOLET }} /> Export PDF bilan
        </button>
        <button onClick={() => setImportKind("expenses")}
          className="flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bilan-import-expenses">
          <Upload className="w-3.5 h-3.5" style={{ color: VIOLET }} /> Importer dépenses
        </button>
        <button onClick={() => setImportKind("income")}
          className="flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bilan-import-income">
          <Upload className="w-3.5 h-3.5" style={{ color: VIOLET }} /> Importer CA historique
        </button>
      </div>
      <div className="bg-white border border-neutral-100 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-x-auto">
        {data.months.some((m) => m.ca_manuel) && (
          <p className="px-4 pt-3 text-[10px] text-neutral-400 flex items-center gap-1.5" data-testid="bilan-manual-legend">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: VIOLET }} />
            Mois où le CA saisi manuellement est retenu (supérieur aux paiements de factures enregistrés).
          </p>
        )}
        <table className="w-full text-xs min-w-[820px]" data-testid="bilan-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
              <th className="px-4 py-2 font-medium">Mois</th>
              {COLS.map(([k, l]) => <th key={k} className="px-2 py-2 font-medium text-right">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.months.map((m) => (
              <tr key={m.key} className="border-b border-neutral-50 hover:bg-neutral-50/50" data-testid={`bilan-row-${m.key}`}>
                <td className="px-4 py-2 font-medium text-neutral-700 capitalize">{frMonth(m.key)}</td>
                {COLS.map(([k]) => (
                  <td key={k} className={`px-2 py-2 text-right ${k === "reste_a_vivre" ? (m[k] >= 0 ? "text-emerald-600 font-medium" : "text-rose-500 font-medium") : "text-neutral-600"}`}>
                    {eur(m[k])}
                    {k === "encaisse" && m.ca_manuel && (
                      <span className="ml-1 inline-block w-2 h-2 rounded-full align-middle" style={{ background: VIOLET }}
                        title="CA saisi manuellement retenu (supérieur aux factures enregistrées)" data-testid={`bilan-manual-${m.key}`} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-[#F8F7FF] font-semibold" data-testid="bilan-total-row">
              <td className="px-4 py-2.5 text-[#0B132B]">TOTAL {year}</td>
              {COLS.map(([k]) => (
                <td key={k} className={`px-2 py-2.5 text-right ${k === "reste_a_vivre" ? (totals[k] >= 0 ? "text-emerald-600" : "text-rose-500") : "text-[#0B132B]"}`}>{eur(totals[k])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="bilan-comparison">
          <h3 className="text-sm font-semibold text-[#0B132B] mb-3">Comparaison {year} vs {year - 1}</h3>
          <ul className="space-y-2">
            {COLS.map(([k, l]) => (
              <li key={k} className="flex items-center justify-between text-xs" data-testid={`bilan-compare-${k}`}>
                <span className="text-neutral-500">{l}</span>
                <span className="flex items-center gap-2">
                  <span className="text-neutral-400">{eur(prev[k] || 0)}</span>
                  <span className="font-medium text-neutral-800">{eur(totals[k])}</span>
                  <DeltaChip cur={totals[k]} prev={prev[k] || 0} />
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="bilan-income-manual">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-sm font-semibold text-[#0B132B] flex items-center gap-1.5"><Landmark className="w-4 h-4" style={{ color: VIOLET }} />CA historique saisi manuellement</h3>
            <button onClick={() => setCaModal(true)} className="flex items-center gap-1.5 text-white text-xs font-medium rounded-lg px-3 py-1.5 shrink-0" style={{ background: VIOLET }} data-testid="bilan-ca-year">
              <Plus className="w-3.5 h-3.5" /> Saisir le CA de l'année
            </button>
          </div>
          <p className="text-[10px] text-neutral-400 mb-3">Pour chaque mois, le montant le plus élevé entre les factures enregistrées et cette saisie est retenu — elle couvre les factures non saisies dans l'app, sans double comptage.</p>
          <div className="flex items-end gap-2 mb-3">
            <label className="text-[11px] text-neutral-500">Mois
              <Input type="month" min={`${year}-01`} max={`${year}-12`} value={income.month} onChange={(e) => setIncome({ ...income, month: e.target.value })} className="h-8 text-xs mt-1 w-36" data-testid="income-month" /></label>
            <label className="text-[11px] text-neutral-500">Montant encaissé (€)
              <Input type="number" min="0" step="0.01" value={income.amount} onChange={(e) => setIncome({ ...income, amount: e.target.value })} className="h-8 text-xs mt-1 w-32" data-testid="income-amount" /></label>
            <Button onClick={addIncome} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="income-add"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
          </div>
          {data.income_manual.length === 0 ? (
            <p className="text-[11px] text-neutral-400 py-3 text-center" data-testid="income-empty">Aucune saisie manuelle — utilisez le formulaire ou l'import CSV.</p>
          ) : (
            <ul className="space-y-1" data-testid="income-list">
              {data.income_manual.map((m) => (
                <li key={m.id} className="flex items-center justify-between text-xs border-b border-neutral-50 py-1.5" data-testid={`income-row-${m.month}`}>
                  <span className="text-neutral-600 capitalize">{new Date(`${m.month}-15`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-neutral-800">{eur(m.amount)}</span>
                    <button onClick={() => delIncome(m.id)} className="p-1 text-neutral-400 hover:text-rose-500" data-testid={`income-del-${m.month}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {importKind && <ImportModal kind={importKind} onClose={() => setImportKind(null)} onDone={load} />}
      {caModal && <CaYearModal year={year} existing={data.income_manual} onClose={() => setCaModal(false)} onDone={load} />}
    </div>
  );
}
